import { supabase } from "./supabase";

/**
 * Reusable Supabase service layer.
 * Every function returns { data, error } (except mutations that throw on error via `throwOnError`).
 * All functions are typed via JSDoc so IDEs pick up the shapes from supabase-types.d.ts.
 */

// ---------- PRODUCTS ------------------------------------------------------
export const ProductsAPI = {
  list: async (opts = {}) => {
    let q = supabase.from("products").select("*");
    if (opts.category) q = q.eq("category", opts.category);
    if (opts.featured) q = q.eq("featured", true);
    if (opts.limit) q = q.limit(opts.limit);
    let res = await q.order("created_at", { ascending: false });
    if (res.error) res = await supabase.from("products").select("*");
    return { data: res.data || [], error: res.error };
  },
  bySlug: async (slug) => {
    const { data, error } = await supabase
      .from("products")
      .select("*")
      .eq("slug", slug)
      .maybeSingle();
    return { data, error };
  },
  byId: async (id) => {
    const { data, error } = await supabase
      .from("products")
      .select("*")
      .eq("id", id)
      .maybeSingle();
    return { data, error };
  },
  // Admin
  create: (payload) => supabase.from("products").insert(payload).select().single(),
  update: (id, patch) => supabase.from("products").update(patch).eq("id", id).select().single(),
  remove: (id) => supabase.from("products").delete().eq("id", id),
  publish: (id, published = true) =>
    supabase
      .from("products")
      .update({ status: published ? "published" : "draft" })
      .eq("id", id)
      .select()
      .single(),
};

// ---------- BLOGS ---------------------------------------------------------
export const BlogsAPI = {
  list: async (opts = {}) => {
    // Try richer query first, fall back to plain select if columns missing.
    let q = supabase.from("blogs").select("*");
    if (opts.status) q = q.eq("status", opts.status);
    if (opts.limit) q = q.limit(opts.limit);
    let res = await q.order("published_at", { ascending: false, nullsFirst: false });
    if (res.error) res = await supabase.from("blogs").select("*");
    return { data: res.data || [], error: res.error };
  },
  bySlug: async (slug) => {
    const { data, error } = await supabase
      .from("blogs")
      .select("*")
      .eq("slug", slug)
      .maybeSingle();
    return { data, error };
  },
  create: (payload) => supabase.from("blogs").insert(payload).select().single(),
  update: (id, patch) => supabase.from("blogs").update(patch).eq("id", id).select().single(),
  remove: (id) => supabase.from("blogs").delete().eq("id", id),
  publish: (id, published = true) =>
    supabase
      .from("blogs")
      .update({
        status: published ? "published" : "draft",
        published_at: published ? new Date().toISOString() : null,
      })
      .eq("id", id)
      .select()
      .single(),
};

// ---------- FAQS ----------------------------------------------------------
export const FaqsAPI = {
  list: async () => {
    // Try preferred (with is_active + sort_order); fall back to plain select
    // so this works against schemas that lack those columns.
    let res = await supabase
      .from("faqs")
      .select("*")
      .eq("is_active", true)
      .order("sort_order", { ascending: true });
    if (res.error) {
      res = await supabase.from("faqs").select("*");
    }
    return { data: res.data || [], error: res.error };
  },
  create: (payload) => supabase.from("faqs").insert(payload).select().single(),
  update: (id, patch) => supabase.from("faqs").update(patch).eq("id", id).select().single(),
  remove: (id) => supabase.from("faqs").delete().eq("id", id),
};

// ---------- CONTACT MESSAGES ---------------------------------------------
export const ContactAPI = {
  submit: (payload) =>
    supabase
      .from("contact_messages")
      .insert({ ...payload, is_read: false, replied: false })
      .select()
      .single(),
};

// ---------- PRE-ORDERS (landing page reservations) -----------------------
export const PreOrdersAPI = {
  submit: (payload) =>
    supabase
      .from("pre_orders")
      .insert({ ...payload, status: payload.status || "new" })
      .select()
      .single(),
  // Admin
  list: async (opts = {}) => {
    let q = supabase
      .from("pre_orders")
      .select("*")
      .order("created_at", { ascending: false });
    if (opts.status) q = q.eq("status", opts.status);
    if (opts.limit) q = q.limit(opts.limit);
    const { data, error } = await q;
    return { data: data || [], error };
  },
  updateStatus: (id, status) =>
    supabase.from("pre_orders").update({ status }).eq("id", id).select().single(),
};

// ---------- NEWSLETTER ----------------------------------------------------
export const NewsletterAPI = {
  subscribe: async (email) => {
    // Upsert on email so re-subscribes don't duplicate rows.
    const { data, error } = await supabase
      .from("newsletter")
      .upsert({ email, subscribed: true }, { onConflict: "email" })
      .select()
      .single();
    return { data, error };
  },
  unsubscribe: (email) =>
    supabase.from("newsletter").update({ subscribed: false }).eq("email", email),
};

// ---------- ORDERS --------------------------------------------------------
export const OrdersAPI = {
  /** Public: fetch a customer's orders by email (RLS should scope this). */
  byEmail: async (email) => {
    const { data, error } = await supabase
      .from("orders")
      .select("*, order_items(*)")
      .eq("email", email)
      .order("created_at", { ascending: false });
    return { data: data || [], error };
  },
  byId: async (id) => {
    const { data, error } = await supabase
      .from("orders")
      .select("*, order_items(*)")
      .eq("id", id)
      .maybeSingle();
    return { data, error };
  },
  // Admin
  list: async (opts = {}) => {
    let q = supabase
      .from("orders")
      .select("*, order_items(*)")
      .order("created_at", { ascending: false });
    if (opts.payment_status) q = q.eq("payment_status", opts.payment_status);
    if (opts.order_status) q = q.eq("order_status", opts.order_status);
    if (opts.limit) q = q.limit(opts.limit);
    const { data, error } = await q;
    return { data: data || [], error };
  },
  updateStatus: (id, patch) =>
    supabase.from("orders").update(patch).eq("id", id).select().single(),
};
